import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, FileText, Link2, Loader2, MessageSquare, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import type { Deal } from '@/types'
import { FinanceC4ContractDeliverablesSection } from '@/components/Financeiro/FinanceC4ContractDeliverablesSection'
import { FinanceRequestCommentsPanel } from '@/components/Financeiro/FinanceRequestCommentsPanel'
import { financeService, type FinanceRequestDetail } from '@/services/financeService'
import { getContractRequestForDeal } from './contractDataFlow'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { formatFinanceRequestStatus } from '@/components/Financeiro/financeC4ContractStatus'
import { FINANCE_GERAR_CONTRATO_MESSAGE } from '@/utils/dealFinanceStageGate'

function formatStatus(status: string) {
  return formatFinanceRequestStatus(status, 'C4_CONTRATO_AGRADA')
}

function deliveryModeLabel(mode?: string | null) {
  if (mode === 'link') return 'Link do contrato'
  if (mode === 'pdf') return 'PDF do contrato'
  return '—'
}

export function DealContractTab({ deal }: { deal: Deal }) {
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<FinanceRequestDetail | null>(null)

  const loadRequest = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getContractRequestForDeal(deal.id)
      setDetail(data)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao carregar chamado do contrato'))
      setDetail(null)
    } finally {
      setLoading(false)
    }
  }, [deal.id])

  useEffect(() => {
    void loadRequest()
  }, [loadRequest])

  const handleAddComment = async (
    body: string,
    _isInternal?: boolean,
    attachments: string[] = [],
  ) => {
    if (!detail) return false
    try {
      const { comment, requestStatus } = await financeService.addRequestComment(
        detail.id,
        body,
        false,
        attachments,
      )
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              comments: [...(prev.comments ?? []), comment],
              ...(requestStatus ? { status: requestStatus } : {}),
            }
          : prev,
      )
      return true
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao enviar comentário'))
      return false
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-text-label">
        <Loader2 size={16} className="animate-spin text-accent" />
        Carregando chamado do contrato...
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="rounded-lg border border-dashed border-white/[0.08] px-4 py-10 text-center">
        <FileText size={28} className="mx-auto mb-3 text-text-muted" />
        <p className="text-sm font-medium text-text-primary">Chamado ainda não disponível</p>
        <p className="mt-1 text-xs text-text-label">
          Ao avançar para Gerar Contrato, um chamado é aberto automaticamente para o financeiro elaborar o documento.
        </p>
      </div>
    )
  }

  const formData =
    detail.formData && typeof detail.formData === 'object'
      ? (detail.formData as unknown as Record<string, unknown>)
      : {}
  const deliveryMode = String(
    formData.contractDeliveryMode ?? deal.contractDeliveryMode ?? '',
  )

  return (
    <div className="space-y-4">
      {deal.etapa === 'gerar_contrato' && (
        <div className="rounded-lg border border-accent/25 bg-accent/10 px-4 py-3 text-sm text-text-primary">
          {FINANCE_GERAR_CONTRATO_MESSAGE}
        </div>
      )}

      <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Chamado do contrato</h3>
            <p className="text-xs text-text-label mt-0.5">
              Protocolo <span className="font-mono text-text-primary">{detail.protocol}</span>
            </p>
          </div>
          <span className="rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-accent">
            {formatStatus(detail.status)}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-label">Formato solicitado</p>
            <p className="mt-0.5 text-text-primary">{deliveryModeLabel(deliveryMode)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-text-label">Responsável</p>
            <p className="mt-0.5 text-text-primary">{detail.assignee?.name ?? 'Financeiro (Janice)'}</p>
          </div>
        </div>

        {(detail.attachments ?? []).length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-label">
              <Paperclip size={12} />
              Anexos da proposta
            </p>
            <ul className="space-y-1">
              {(detail.attachments ?? []).map((url) => (
                <li key={url}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <ExternalLink size={12} />
                    Ver anexo
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-label">
          <Link2 size={14} className="text-accent" />
          Contrato preparado pelo financeiro
        </h3>
        <FinanceC4ContractDeliverablesSection
          detail={detail}
          canManage={false}
          showDeliveryMode={false}
          onReload={() => void loadRequest()}
        />
      </section>

      <section className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
        <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-label">
          <MessageSquare size={14} className="text-accent" />
          Comunicação com o financeiro
        </h3>
        <FinanceRequestCommentsPanel
          detail={detail}
          canManageQueue={false}
          onAddComment={handleAddComment}
          compact
        />
      </section>
    </div>
  )
}
